import { useState } from 'react'
import { motion } from 'framer-motion'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { QRCodeSVG } from 'qrcode.react'
import { Copy, CheckCircle } from 'lucide-react'
import { useTransactions } from '@/state/transactions'
import { useSystemDefaults } from '@/state/system'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from '@/hooks/useTranslation'

const getErrorMessage = (error: unknown): string => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return 'An error occurred'
}

interface SearchParams {
  transaction_id?: string;
}

export default function CompletePendingPaymentPage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false }) as SearchParams
  const transactionId = search.transaction_id
  const [txId, setTxId] = useState('')
  const [copied, setCopied] = useState(false)
  
  const { 
    completePurchase, 
    completePurchaseLoading, 
    completePurchaseError,
    completePurchaseSuccess 
  } = useTransactions()
  
  const { getWalletAddress } = useSystemDefaults()
  const walletAddress = String(getWalletAddress() || '')

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (transactionId && txId) {
      completePurchase({
        transaction_id: parseInt(transactionId),
        txId
      })
    }
  }

  const handleCopy = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (completePurchaseSuccess) {
    return (
      <div className="p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto"
        >
          <Card className="p-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">{t('pages.completePendingPayment.paymentCompleted')}</h1>
            <p className="text-muted-foreground">
              {t('pages.completePendingPayment.paymentProcessed')}
            </p>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <h1 className="text-3xl font-bold mb-6 text-center">{t('pages.completePendingPayment.completePayment')}</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Instructions */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">{t('pages.completePendingPayment.paymentInstructions')}</h2>
            
            {walletAddress && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t('pages.completePendingPayment.walletAddress')}</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input 
                      value={walletAddress} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="text-center">
                  <QRCodeSVG 
                    value={walletAddress} 
                    size={200}
                    className="mx-auto border rounded-lg p-4"
                  />
                </div>
              </div>
            )}

            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="font-medium text-yellow-800 mb-2">{t('pages.completePendingPayment.important')}</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• {t('pages.completePendingPayment.instructions.sendExactAmount')}</li>
                <li>• {t('pages.completePendingPayment.instructions.copyAddress')}</li>
                <li>• {t('pages.completePendingPayment.instructions.enterTxId')}</li>
              </ul>
            </div>
          </Card>

          {/* Transaction ID Submission */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">{t('pages.completePendingPayment.submitTransactionId')}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('pages.completePendingPayment.transactionId')}</label>
                <Input
                  placeholder={t('pages.completePendingPayment.enterTransactionId')}
                  value={txId}
                  onChange={(e) => setTxId(e.target.value)}
                  required
                />
              </div>

              {completePurchaseError ? (
                <div className="text-red-600 text-sm">
                  {getErrorMessage(completePurchaseError) || t('pages.completePendingPayment.failedToComplete')}
                </div>
              ) : null}

              <Button 
                type="submit" 
                className="w-full"
                disabled={completePurchaseLoading || !txId}
              >
                {completePurchaseLoading ? t('pages.completePendingPayment.processing') : t('pages.completePendingPayment.completePurchaseButton')}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-2">{t('pages.completePendingPayment.needHelp')}</h3>
              <p className="text-sm text-blue-700">
                {t('pages.completePendingPayment.helpText')}
              </p>
            </div>
          </Card>
        </div>
      </motion.div>
    </div>
  )
}